import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
  Typography
} from '@mui/material';
import {
  FormatColorFill as HighlightIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

// Cores disponíveis para highlight
const HIGHLIGHT_COLORS = [
  { name: 'Amarelo', value: '#ffeb3b', textColor: '#000' },
  { name: 'Verde', value: '#81c784', textColor: '#000' },
  { name: 'Azul', value: '#64b5f6', textColor: '#000' },
  { name: 'Rosa', value: '#f48fb1', textColor: '#000' },
  { name: 'Laranja', value: '#ffb74d', textColor: '#000' }
];

// Cores para briefing highlights (fixas)
const BRIEFING_COLORS = {
  primary: '#5f9c45',
  competitor: '#4f6f8f'
};

const SEMANTIC_HIGHLIGHT_COLORS = {
  spokesperson: '#3b82f6',
  keyMessage: '#8b5cf6'
};

const getBriefingTextStyle = (type) => `
  background-color: transparent;
  color: ${type === 'primary' ? BRIEFING_COLORS.primary : BRIEFING_COLORS.competitor};
  padding: 0;
  border-radius: 0;
  cursor: pointer;
  border-bottom: 0;
  font-weight: 800;
`;

const getSemanticTextStyle = (type) => {
  const color = SEMANTIC_HIGHLIGHT_COLORS[type] || '#64748b';
  return `
    background-color: ${hexToRgba(color, 0.18)};
    color: inherit;
    padding: 0 3px;
    border-radius: 4px;
    border-bottom: 2px solid ${color};
    cursor: help;
    font-weight: 750;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  `;
};

const hexToRgba = (hex, alpha = 0.22) => {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map(char => char + char).join('')
    : normalized;
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getStoredHighlightStyle = (color) => `
  background-color: ${hexToRgba(color, 0.24)};
  color: inherit;
  padding: 0 3px;
  border-radius: 4px;
  cursor: pointer;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

const escapeRegExp = (string) => String(string || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getSpokespersonVariants = (value) => {
  const clean = String(value || '').replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  const variants = [clean];

  if (parts.length >= 3) {
    variants.push(`${parts[0]} ${parts[parts.length - 1]}`);
  }

  return [...new Set(variants.filter(variant => variant.length >= 3))];
};

const getMessageHighlightTerms = (message) => {
  const cleanMessage = String(message || '').replace(/\s+/g, ' ').trim();
  if (cleanMessage.length < 12) return [];

  // Mensagem-chave nao deve virar caca-palavras: so destacamos uma frase/trecho
  // continuo quando ela aparece literalmente no artigo.
  return [cleanMessage];
};

/**
 * Componente de marca-texto (highlighter) estilo Pocket
 * Permite selecionar texto e destacá-lo com cores
 * Também suporta highlighting automático de keywords do briefing
 */
const TextHighlighter = ({
  containerRef,
  highlights = [],
  onAddHighlight,
  onRemoveHighlight,
  articleId,
  isEnabled = true,
  briefingKeywords = { primary: [], competitor: [] },
  showBriefingHighlights = false,
  semanticHighlights = [],
  contentDependency = null
}) => {
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedColor, setSelectedColor] = useState(HIGHLIGHT_COLORS[0].value);
  const [editingHighlight, setEditingHighlight] = useState(null);
  const [clickedBriefingKeyword, setClickedBriefingKeyword] = useState(null);

  // Aplicar highlights salvos ao conteúdo
  const applyStoredHighlights = useCallback(() => {
    if (!containerRef?.current || highlights.length === 0) return;

    const container = containerRef.current;

    // Limpar highlights anteriores primeiro
    const existingHighlights = container.querySelectorAll('.stored-highlight');
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      const text = document.createTextNode(el.textContent);
      parent.replaceChild(text, el);
    });

    // Normalizar o container para juntar text nodes adjacentes
    container.normalize();

    // Aplicar cada highlight salvo
    highlights.forEach(highlight => {
      try {
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null
        );

        let node;
        while (node = walker.nextNode()) {
          const nodeText = node.textContent;
          const index = nodeText.indexOf(highlight.text);

          if (index !== -1) {
            const range = document.createRange();
            range.setStart(node, index);
            range.setEnd(node, index + highlight.text.length);

            const mark = document.createElement('mark');
            mark.className = 'stored-highlight';
            mark.dataset.highlightId = highlight._id;
            mark.style.cssText = getStoredHighlightStyle(highlight.color);
            mark.textContent = highlight.text;

            range.deleteContents();
            range.insertNode(mark);
            break; // Só aplicar uma vez por highlight
          }
        }
      } catch (e) {
        console.log('Erro ao aplicar highlight:', e);
      }
    });
  }, [containerRef, highlights]);

  // Aplicar briefing highlights (keywords do briefing)
  const applyBriefingHighlights = useCallback(() => {
    if (!containerRef?.current || !showBriefingHighlights) return;

    const container = containerRef.current;

    // Limpar briefing highlights anteriores
    const existingBriefingHighlights = container.querySelectorAll('.briefing-highlight');
    existingBriefingHighlights.forEach(el => {
      const parent = el.parentNode;
      const text = document.createTextNode(el.textContent);
      parent.replaceChild(text, el);
    });

    container.normalize();

    // Obter o texto completo do container uma vez para otimizar a checagem de requiresWith
    const fullContainerText = container.textContent.toLowerCase();

    // Aplicar primary keywords (marca)
    briefingKeywords.primary.forEach(kwData => {
      try {
        // Condição vitalícia: se tem requiresWith, só grifa se achar a palavra vinculada no texto!
        if (kwData.requiresWith && kwData.requiresWith.trim()) {
          if (!fullContainerText.includes(kwData.requiresWith.toLowerCase())) {
            return; // Aborta e não grifa essa palavra
          }
        }

        // Função auxiliar para escapar caracteres especiais de regex
        const escapeRegExp = (string) => {
          return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        };
        const escapedKeyword = escapeRegExp(kwData.keyword);
        
        // Se isExact for true, exige limites sem ser letra/numero, garantindo match perfeito independente de pontuação
        const regexPattern = kwData.isExact 
          ? `(?<=^|[^\\\\p{L}\\\\p{N}_])(${escapedKeyword})(?=[^\\\\p{L}\\\\p{N}_]|$)` 
          : `(${escapedKeyword})`;
        const regex = new RegExp(regexPattern, 'gui');

        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              // Ignorar nodes que já estão dentro de highlights
              if (node.parentElement?.closest('.stored-highlight, .briefing-highlight, .semantic-highlight')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Ignorar área de tags e outros elementos da UI
              if (node.parentElement?.closest('.MuiChip-root, [class*="tag"], [class*="Tag"]')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Aceitar todos os outros nós de texto (o matching será feito depois)
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );

        let node;
        while (node = walker.nextNode()) {
          const matches = [...node.textContent.matchAll(regex)];

          // Processar matches de trás para frente para não afetar índices
          matches.reverse().forEach(match => {
            try {
              const range = document.createRange();
              range.setStart(node, match.index);
              range.setEnd(node, match.index + match[0].length);

              const mark = document.createElement('mark');
              mark.className = 'briefing-highlight briefing-primary';
              mark.dataset.briefingType = 'primary';
              mark.dataset.keyword = kwData.keyword;
              mark.title = `🟢 Palavra-chave da marca: ${kwData.keyword}`;
              mark.style.cssText = getBriefingTextStyle('primary');
              mark.textContent = match[0];

              range.deleteContents();
              range.insertNode(mark);
            } catch (e) {
              console.log('Erro ao aplicar primary highlight:', e);
            }
          });
        }
      } catch (e) {
        console.log('Erro ao processar primary keyword:', kwData.keyword, e);
      }
    });

    // Aplicar competitor keywords
    briefingKeywords.competitor.forEach(kwData => {
      try {
        if (kwData.requiresWith && kwData.requiresWith.trim()) {
          if (!fullContainerText.includes(kwData.requiresWith.toLowerCase())) {
            return;
          }
        }

        const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const escapedKeyword = escapeRegExp(kwData.keyword);
        
        const regexPattern = kwData.isExact 
          ? `(?<=^|[^\\\\p{L}\\\\p{N}_])(${escapedKeyword})(?=[^\\\\p{L}\\\\p{N}_]|$)` 
          : `(${escapedKeyword})`;
        const regex = new RegExp(regexPattern, 'gui');

        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              if (node.parentElement?.closest('.stored-highlight, .briefing-highlight, .semantic-highlight')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Ignorar área de tags e outros elementos da UI
              if (node.parentElement?.closest('.MuiChip-root, [class*="tag"], [class*="Tag"]')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Aceitar todos os outros nós de texto (o matching será feito depois)
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );

        let node;
        while (node = walker.nextNode()) {
          const matches = [...node.textContent.matchAll(regex)];

          matches.reverse().forEach(match => {
            try {
              const range = document.createRange();
              range.setStart(node, match.index);
              range.setEnd(node, match.index + match[0].length);

              const mark = document.createElement('mark');
              mark.className = 'briefing-highlight briefing-competitor';
              mark.dataset.briefingType = 'competitor';
              mark.dataset.keyword = kwData.keyword;
              mark.dataset.competitor = kwData.competitor;
              mark.title = `🔵 Concorrente: ${kwData.competitor} | Palavra-chave: ${kwData.keyword}`;
              mark.style.cssText = getBriefingTextStyle('competitor');
              mark.textContent = match[0];

              range.deleteContents();
              range.insertNode(mark);
            } catch (e) {
              console.log('Erro ao aplicar competitor highlight:', e);
            }
          });
        }
      } catch (e) {
        console.log('Erro ao processar competitor keyword:', kwData.keyword, e);
      }
    });
  }, [containerRef, showBriefingHighlights, briefingKeywords]);

  const applySemanticHighlights = useCallback(() => {
    if (!containerRef?.current) return;

    const container = containerRef.current;
    const existingSemanticHighlights = container.querySelectorAll('.semantic-highlight');
    existingSemanticHighlights.forEach(el => {
      const parent = el.parentNode;
      const text = document.createTextNode(el.textContent);
      parent.replaceChild(text, el);
    });

    container.normalize();

    if (!semanticHighlights || semanticHighlights.length === 0) return;

    const candidates = [];
    semanticHighlights.forEach(item => {
      if (!item?.text) return;
      const type = item.type === 'spokesperson' ? 'spokesperson' : 'keyMessage';
      const label = item.label || (type === 'spokesperson' ? 'Porta-voz' : 'Mensagem-chave');
      const terms = type === 'spokesperson'
        ? getSpokespersonVariants(item.text)
        : getMessageHighlightTerms(item.text);

      terms.forEach(term => {
        if (term && term.length >= 2) {
          candidates.push({
            type,
            label,
            sourceText: item.text,
            term
          });
        }
      });
    });

    candidates
      .sort((a, b) => b.term.length - a.term.length)
      .forEach(candidate => {
        try {
          const escapedTerm = escapeRegExp(candidate.term);
          const regex = new RegExp(`(?<=^|[^\\p{L}\\p{N}_])(${escapedTerm})(?=[^\\p{L}\\p{N}_]|$)`, 'gui');
          const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) => {
                if (node.parentElement?.closest('.stored-highlight, .briefing-highlight, .semantic-highlight')) {
                  return NodeFilter.FILTER_REJECT;
                }
                if (node.parentElement?.closest('.MuiChip-root, [class*="tag"], [class*="Tag"]')) {
                  return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
              }
            }
          );

          let node;
          while (node = walker.nextNode()) {
            const matches = [...node.textContent.matchAll(regex)];

            matches.reverse().forEach(match => {
              try {
                const range = document.createRange();
                range.setStart(node, match.index);
                range.setEnd(node, match.index + match[0].length);

                const mark = document.createElement('mark');
                mark.className = `semantic-highlight semantic-${candidate.type}`;
                mark.dataset.semanticType = candidate.type;
                mark.dataset.semanticLabel = candidate.label;
                mark.dataset.semanticText = candidate.sourceText;
                mark.title = `${candidate.label}: ${candidate.sourceText}`;
                mark.style.cssText = getSemanticTextStyle(candidate.type);
                mark.textContent = match[0];

                range.deleteContents();
                range.insertNode(mark);
              } catch (e) {
                console.log('Erro ao aplicar semantic highlight:', e);
              }
            });
          }
        } catch (e) {
          console.log('Erro ao processar semantic highlight:', candidate.term, e);
        }
      });
  }, [containerRef, semanticHighlights]);

  // Aplicar highlights quando mudam
  useEffect(() => {
    if (containerRef?.current && highlights.length > 0) {
      // Pequeno delay para garantir que o conteúdo está renderizado
      const timer = setTimeout(applyStoredHighlights, 500);
      return () => clearTimeout(timer);
    }
  }, [highlights, applyStoredHighlights, contentDependency]);

  // Aplicar briefing highlights quando mudam
  useEffect(() => {
    if (containerRef?.current && showBriefingHighlights && briefingKeywords && articleId) {
      // Delay maior para garantir que o conteúdo React foi renderizado
      const timer = setTimeout(applyBriefingHighlights, 1000);
      return () => clearTimeout(timer);
    } else if (containerRef?.current && !showBriefingHighlights) {
      // Remover briefing highlights se toggle foi desativado
      const existingBriefingHighlights = containerRef.current.querySelectorAll('.briefing-highlight');
      existingBriefingHighlights.forEach(el => {
        const parent = el.parentNode;
        const text = document.createTextNode(el.textContent);
        parent.replaceChild(text, el);
      });
      containerRef.current.normalize();
    }
  }, [showBriefingHighlights, briefingKeywords, applyBriefingHighlights, containerRef, articleId, contentDependency]);

  useEffect(() => {
    if (containerRef?.current && articleId) {
      const timer = setTimeout(applySemanticHighlights, 1150);
      return () => clearTimeout(timer);
    }
  }, [semanticHighlights, applySemanticHighlights, containerRef, articleId, contentDependency]);

  // Detectar seleção de texto
  const handleMouseUp = useCallback((e) => {
    if (!isEnabled || !containerRef?.current) return;

    // Verificar se clicou em um briefing highlight
    const clickedBriefingHighlight = e.target.closest('.briefing-highlight');
    if (clickedBriefingHighlight) {
      const briefingType = clickedBriefingHighlight.dataset.briefingType;
      const keyword = clickedBriefingHighlight.dataset.keyword;
      const competitor = clickedBriefingHighlight.dataset.competitor;

      setClickedBriefingKeyword({
        type: briefingType,
        keyword,
        competitor
      });
      setEditingHighlight(null);
      setPopoverAnchor(clickedBriefingHighlight);
      return;
    }

    // Verificar se clicou em um highlight existente (manual)
    const clickedHighlight = e.target.closest('.stored-highlight');
    if (clickedHighlight) {
      const highlightId = clickedHighlight.dataset.highlightId;
      const highlight = highlights.find(h => h._id === highlightId);
      if (highlight) {
        setEditingHighlight(highlight);
        setClickedBriefingKeyword(null);
        setPopoverAnchor(clickedHighlight);
        return;
      }
    }

    const selection = window.getSelection();
    const text = selection?.toString()?.trim();

    if (text && text.length > 2) {
      // Verificar se a seleção está dentro do container
      const range = selection.getRangeAt(0);
      if (containerRef.current.contains(range.commonAncestorContainer)) {
        setSelectedText(text);
        setSelectedRange(range.cloneRange());
        setEditingHighlight(null);

        // Posicionar popover usando virtualElement (não remove o âncora)
        const rect = range.getBoundingClientRect();
        const virtualElement = {
          getBoundingClientRect: () => ({
            top: rect.top,
            left: rect.left + rect.width / 2,
            bottom: rect.bottom,
            right: rect.right,
            width: 0,
            height: 0
          })
        };

        setPopoverAnchor(virtualElement);
      }
    }
  }, [isEnabled, containerRef, highlights]);

  // Adicionar listener de mouseup
  useEffect(() => {
    if (!containerRef?.current || !isEnabled) return;

    const container = containerRef.current;
    container.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mouseup', handleMouseUp);
    };
  }, [containerRef, isEnabled, handleMouseUp]);

  // Fechar popover (deve vir antes das funções que a usam)
  const handleClosePopover = useCallback(() => {
    setPopoverAnchor(null);
    setSelectedText('');
    setSelectedRange(null);
    setEditingHighlight(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  // Criar highlight
  const handleCreateHighlight = useCallback(async () => {
    if (!selectedText || !selectedRange) {
      console.log('TextHighlighter: selectedText ou selectedRange vazio');
      return;
    }

    const newHighlight = {
      text: selectedText,
      color: selectedColor,
      startOffset: selectedRange.startOffset,
      endOffset: selectedRange.endOffset
    };

    console.log('TextHighlighter: Criando highlight:', newHighlight);

    // Aplicar highlight visualmente primeiro (optimistic)
    let mark = null;
    try {
      mark = document.createElement('mark');
      mark.className = 'stored-highlight temp-highlight';
      mark.style.cssText = getStoredHighlightStyle(selectedColor);

      selectedRange.surroundContents(mark);
      window.getSelection()?.removeAllRanges();
    } catch (e) {
      console.log('TextHighlighter: Erro ao aplicar highlight visual:', e);
    }

    // Fechar popover
    handleClosePopover();

    // Salvar no backend
    try {
      const savedHighlight = await onAddHighlight?.(newHighlight);
      console.log('TextHighlighter: Highlight salvo:', savedHighlight);

      // Atualizar o data-attribute com o ID real
      if (mark && savedHighlight?._id) {
        mark.dataset.highlightId = savedHighlight._id;
        mark.classList.remove('temp-highlight');
      }
    } catch (error) {
      console.error('TextHighlighter: Erro ao salvar highlight:', error);
      // Remover highlight visual em caso de erro
      if (mark && mark.parentNode) {
        const parent = mark.parentNode;
        const text = document.createTextNode(mark.textContent);
        parent.replaceChild(text, mark);
        parent.normalize();
      }
    }
  }, [selectedText, selectedRange, selectedColor, onAddHighlight, handleClosePopover]);

  // Remover highlight
  const handleRemoveHighlight = useCallback(() => {
    if (!editingHighlight) return;

    onRemoveHighlight?.(editingHighlight._id);

    // Remover visualmente
    const mark = document.querySelector(`[data-highlight-id="${editingHighlight._id}"]`);
    if (mark) {
      const parent = mark.parentNode;
      const text = document.createTextNode(mark.textContent);
      parent.replaceChild(text, mark);
      parent.normalize();
    }

    handleClosePopover();
  }, [editingHighlight, onRemoveHighlight, handleClosePopover]);

  // Não renderizar se não está habilitado
  if (!isEnabled) return null;

  return (
    <Popover
      open={Boolean(popoverAnchor)}
      anchorEl={popoverAnchor}
      onClose={handleClosePopover}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          p: 1,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
        }
      }}
    >
      {clickedBriefingKeyword ? (
        // Menu para briefing keyword (informativo)
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">
            {clickedBriefingKeyword.type === 'primary' ? '🟢 Palavra-chave da Marca' : '🔵 Concorrente'}
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {clickedBriefingKeyword.keyword}
          </Typography>
          {clickedBriefingKeyword.competitor && (
            <Typography variant="caption" color="text.secondary">
              Concorrente: {clickedBriefingKeyword.competitor}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            Destaque automático do briefing
          </Typography>
        </Box>
      ) : editingHighlight ? (
        // Menu para highlight existente
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Destaque
          </Typography>
          <Tooltip title="Remover destaque">
            <IconButton
              size="small"
              color="error"
              onClick={handleRemoveHighlight}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        // Menu para criar novo highlight
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Destacar texto">
            <IconButton
              size="small"
              onClick={handleCreateHighlight}
              sx={{ color: selectedColor }}
            >
              <HighlightIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Seletor de cores */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {HIGHLIGHT_COLORS.map(color => (
              <Tooltip key={color.value} title={color.name}>
                <Box
                  onClick={() => setSelectedColor(color.value)}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: color.value,
                    cursor: 'pointer',
                    border: selectedColor === color.value ? '2px solid #333' : '1px solid #ccc',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.2)'
                    }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}
    </Popover>
  );
};

TextHighlighter.propTypes = {
  containerRef: PropTypes.object,
  highlights: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    text: PropTypes.string,
    color: PropTypes.string,
    startOffset: PropTypes.number,
    endOffset: PropTypes.number
  })),
  onAddHighlight: PropTypes.func,
  onRemoveHighlight: PropTypes.func,
  articleId: PropTypes.string,
  isEnabled: PropTypes.bool,
  briefingKeywords: PropTypes.shape({
    primary: PropTypes.arrayOf(PropTypes.shape({
      keyword: PropTypes.string,
      weight: PropTypes.number,
      isExact: PropTypes.bool
    })),
    competitor: PropTypes.arrayOf(PropTypes.shape({
      keyword: PropTypes.string,
      competitor: PropTypes.string
    }))
  }),
  showBriefingHighlights: PropTypes.bool,
  semanticHighlights: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['spokesperson', 'keyMessage']).isRequired,
    text: PropTypes.string.isRequired,
    label: PropTypes.string
  })),
  contentDependency: PropTypes.any
};

export default TextHighlighter;
